import React, { Suspense } from 'react'
import './App.css'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { Box } from '@chakra-ui/react'
import { useAuth } from './auth/AuthContext'
import Header, { HEADER_HEIGHT } from './Header/index'
import Footer from './components/Footer'

/**
 * Wraps React.lazy with a single retry on chunk load failure.
 * If the import fails (stale deployment / CDN propagation lag), we reload
 * the page once — after that the browser has the fresh index.html and
 * the new chunk URLs, so the retry will succeed.
 */
function lazyWithReload<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().catch(() => {
      const RELOAD_KEY = 'chunk_reload_at'
      const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
      if (Date.now() - last > 15_000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
        window.location.reload()
      }
      // Promise that never resolves — the reload above will restart the app
      return new Promise<{ default: T }>(() => {})
    })
  )
}

// Public pages
const Home = lazyWithReload(() => import('./pages/Home'))
const Marketplace = lazyWithReload(() => import('./pages/Marketplace'))
const ModelDetail = lazyWithReload(() => import('./pages/ModelDetail'))
const UserProfile = lazyWithReload(() => import('./pages/UserProfile'))
const Cart = lazyWithReload(() => import('./pages/Cart'))
const Login = lazyWithReload(() => import('./NonAuthorized/Login'))
const Register = lazyWithReload(() => import('./NonAuthorized/Register'))
const OrderSuccess = lazyWithReload(() => import('./pages/OrderSuccess'))
const Services = lazyWithReload(() => import('./pages/Services'))
const HelpCenter = lazyWithReload(() => import('./pages/HelpCenter'))
const PrivacyPolicy = lazyWithReload(() => import('./NonAuthorized/PrivacyPolicy'))
const TermsOfUse = lazyWithReload(() => import('./NonAuthorized/TermsOfUse'))

// Blog & Guides
const Blog = lazyWithReload(() => import('./pages/Blog'))
const PrinterGuide = lazyWithReload(() => import('./pages/guides/PrinterGuide'))
const FilamentGuide = lazyWithReload(() => import('./pages/guides/FilamentGuide'))

// Material pages
const MaterialPLA = lazyWithReload(() => import('./pages/materials/PLA'))
const MaterialPETG = lazyWithReload(() => import('./pages/materials/PETG'))
const MaterialABS = lazyWithReload(() => import('./pages/materials/ABS'))
const MaterialTPU = lazyWithReload(() => import('./pages/materials/TPU'))
const MaterialASA = lazyWithReload(() => import('./pages/materials/ASA'))
const MaterialRecine = lazyWithReload(() => import('./pages/materials/Recine'))

// Company public pages
const Companies = lazyWithReload(() => import('./pages/Companies'))
const CompanyDetail = lazyWithReload(() => import('./pages/CompanyDetail'))

// Designer public pages
const Designers = lazyWithReload(() => import('./pages/Designers'))
const DesignerDetail = lazyWithReload(() => import('./pages/DesignerDetail'))

// Authenticated pages
const Dashboard = lazyWithReload(() => import('./pages/User/Dashboard'))
const Upload = lazyWithReload(() => import('./pages/User/Upload'))
const MyModels = lazyWithReload(() => import('./pages/User/MyModels'))
const Orders = lazyWithReload(() => import('./pages/User/Orders'))
const OrderDetail = lazyWithReload(() => import('./pages/User/OrderDetail'))
const Checkout = lazyWithReload(() => import('./pages/Checkout'))
const Bookmarks = lazyWithReload(() => import('./pages/User/Bookmarks'))
const Settings = lazyWithReload(() => import('./pages/User/Settings'))
const PublishModel = lazyWithReload(() => import('./pages/User/PublishModel'))
const DesignerProfile = lazyWithReload(() => import('./pages/User/DesignerProfile'))
const Messages = lazyWithReload(() => import('./pages/User/Messages'))
const Commissions = lazyWithReload(() => import('./pages/User/Commissions'))
const CommissionDetail = lazyWithReload(() => import('./pages/User/CommissionDetail'))

// Company dashboard pages
const CompanyDashboard = lazyWithReload(() => import('./pages/Company/Dashboard'))
const CompanyOrders = lazyWithReload(() => import('./pages/Company/Orders'))
const CompanyOrderDetail = lazyWithReload(() => import('./pages/Company/OrderDetail'))
const CompanyMessages = lazyWithReload(() => import('./pages/Company/Messages'))
const CompanyProfile = lazyWithReload(() => import('./pages/Company/Profile'))

// Admin pages
const AdminOrders = lazyWithReload(() => import('./pages/Admin/Orders'))
const AdminMaterials = lazyWithReload(() => import('./pages/Admin/Materials'))
const AdminPrintOptions = lazyWithReload(() => import('./pages/Admin/PrintOptions'))
const AdminPricing = lazyWithReload(() => import('./pages/Admin/Pricing'))
const AdminShippingRates = lazyWithReload(() => import('./pages/Admin/ShippingRates'))
const AdminCompanies = lazyWithReload(() => import('./pages/Admin/Companies'))
const BlogManagement = lazyWithReload(() => import('./Authorized/Admin/BlogManagement'))

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (!user) return <Navigate to="/tr/giris" replace />
  return <>{children}</>
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (!user) return <Navigate to="/tr/giris" replace />
  if (!user.is_admin) return <Navigate to="/tr/anasayfa" replace />
  return <>{children}</>
}

function RequirePrintCompany({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (!user) return <Navigate to="/tr/giris" replace />
  if (user.account_type !== 'print_company') return <Navigate to="/tr/anasayfa" replace />
  return <>{children}</>
}

function Layout() {
  return (
    <>
      <Header />
      <Box as="main" pt={`${HEADER_HEIGHT}px`} minH="100vh" bg="black">
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </Box>
      <Footer />
    </>
  )
}

function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        {/* Redirect root */}
        <Route path="/" element={<Navigate to="/tr/anasayfa" replace />} />
        <Route path="/tr" element={<Navigate to="/tr/anasayfa" replace />} />

        {/* Public — no login required */}
        <Route path="/tr/anasayfa" element={<Home />} />
        <Route path="/tr/pazar" element={<Marketplace />} />
        <Route path="/tr/pazar/:id" element={<ModelDetail />} />
        <Route path="/tr/kullanici/:username" element={<UserProfile />} />
        <Route path="/tr/sepet" element={<Cart />} />
        <Route path="/tr/giris" element={<Login />} />
        <Route path="/tr/kayit" element={<Register />} />
        <Route path="/tr/siparis-tamamlandi" element={<OrderSuccess />} />
        <Route path="/tr/hizmetler" element={<Services />} />
        <Route path="/tr/yardim" element={<HelpCenter />} />
        <Route path="/tr/gizlilik" element={<PrivacyPolicy />} />
        <Route path="/tr/kullanim-kosullari" element={<TermsOfUse />} />

        {/* Company public pages */}
        <Route path="/tr/firmalar" element={<Companies />} />
        <Route path="/tr/firmalar/:username" element={<CompanyDetail />} />

        {/* Designer public pages */}
        <Route path="/tr/tasarimcilar" element={<Designers />} />
        <Route path="/tr/tasarimcilar/:username" element={<DesignerDetail />} />

        {/* Blog & Guides */}
        <Route path="/tr/blog" element={<Blog />} />
        <Route path="/tr/rehber/3d-yazici-secimi" element={<PrinterGuide />} />
        <Route path="/tr/rehber/filament-secimi" element={<FilamentGuide />} />

        {/* Material pages */}
        <Route path="/tr/malzeme/pla" element={<MaterialPLA />} />
        <Route path="/tr/malzeme/petg" element={<MaterialPETG />} />
        <Route path="/tr/malzeme/abs" element={<MaterialABS />} />
        <Route path="/tr/malzeme/tpu" element={<MaterialTPU />} />
        <Route path="/tr/malzeme/asa" element={<MaterialASA />} />
        <Route path="/tr/malzeme/recine" element={<MaterialRecine />} />

        {/* Authenticated */}
        <Route path="/tr/hesabim" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/tr/yukle" element={<RequireAuth><Upload /></RequireAuth>} />
        <Route path="/tr/modellerim" element={<RequireAuth><MyModels /></RequireAuth>} />
        <Route path="/tr/siparislerim" element={<RequireAuth><Orders /></RequireAuth>} />
        <Route path="/tr/siparisler/:id" element={<RequireAuth><OrderDetail /></RequireAuth>} />
        <Route path="/tr/odeme" element={<RequireAuth><Checkout /></RequireAuth>} />
        <Route path="/tr/kaydedilenler" element={<RequireAuth><Bookmarks /></RequireAuth>} />
        <Route path="/tr/ayarlar" element={<RequireAuth><Settings /></RequireAuth>} />
        <Route path="/tr/model-yayinla/:id" element={<RequireAuth><PublishModel /></RequireAuth>} />
        <Route path="/tr/tasarimci-profili" element={<RequireAuth><DesignerProfile /></RequireAuth>} />
        <Route path="/tr/mesajlar" element={<RequireAuth><Messages /></RequireAuth>} />
        <Route path="/tr/komisyonlar" element={<RequireAuth><Commissions /></RequireAuth>} />
        <Route path="/tr/komisyonlar/:id" element={<RequireAuth><CommissionDetail /></RequireAuth>} />

        {/* Company dashboard */}
        <Route path="/tr/firma" element={<RequirePrintCompany><CompanyDashboard /></RequirePrintCompany>} />
        <Route path="/tr/firma/siparisler" element={<RequirePrintCompany><CompanyOrders /></RequirePrintCompany>} />
        <Route path="/tr/firma/siparisler/:id" element={<RequirePrintCompany><CompanyOrderDetail /></RequirePrintCompany>} />
        <Route path="/tr/firma/mesajlar" element={<RequirePrintCompany><CompanyMessages /></RequirePrintCompany>} />
        <Route path="/tr/firma/profil" element={<RequirePrintCompany><CompanyProfile /></RequirePrintCompany>} />

        {/* Admin */}
        <Route path="/tr/admin" element={<RequireAdmin><AdminOrders /></RequireAdmin>} />
        <Route path="/tr/admin/siparisler" element={<RequireAdmin><AdminOrders /></RequireAdmin>} />
        <Route path="/tr/admin/malzemeler" element={<RequireAdmin><AdminMaterials /></RequireAdmin>} />
        <Route path="/tr/admin/baski-secenekleri" element={<RequireAdmin><AdminPrintOptions /></RequireAdmin>} />
        <Route path="/tr/admin/fiyatlandirma" element={<RequireAdmin><AdminPricing /></RequireAdmin>} />
        <Route path="/tr/admin/kargo" element={<RequireAdmin><AdminShippingRates /></RequireAdmin>} />
        <Route path="/tr/admin/blog" element={<RequireAdmin><BlogManagement /></RequireAdmin>} />
        <Route path="/tr/admin/firmalar" element={<RequireAdmin><AdminCompanies /></RequireAdmin>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/tr/anasayfa" replace />} />
      </Route>
    </Routes>
  )
}

export default App
